import type { OnboardingStatusAPIType } from '../vendor-portal/onboarding/types';

export type VendorDataType = {
  vendor_id: string;
  first_name: string;
  last_name: string;
  email: string;
  company_name: string;
  link: string;
  address: string;
  status: {
    slug: string;
    title: string;
  };
  phone_number: string;
  gl_insurance_expiry: string;
  is_active: boolean;
  company_address: {
    street_address?: string | null;
    apt_suite_unit?: string;
    city?: string | null;
    state_code?: string | null;
    zip_code?: string | null;
  };
  markets?: Array<{
    id: string;
    name: string;
  }>;
  onboarding: {
    vendor_onboarding_id?: string;
    status?: OnboardingStatusAPIType;
  };
  // Vendors sourced from a property management system (Yardi/AppFolio). The PMS
  // owns their contact/address data, so anything it left blank cannot be fixed
  // from Foresight and blocks the onboarding invite.
  is_synced: boolean;
  missing_onboarding_fields: string[];
};

type VendorMetaType = {
  current_page: number;
  from: number;
  last: string;
  next: string;
  per_page: number;
  prev: string;
  to: number;
  total: number;
};

export type VendorPusherDataType = {
  vendor_id: string;
  onboarding: {
    onboarding_id: string;
    status: OnboardingStatusAPIType;
  };
};

// ---- Vendors waiting for approval (6.11, 6.12) ----------------------------------------------

export type VendorWorkApproval = 'pending' | 'approved' | 'declined';

/** A trade the import suggested from the vendor's PMS category; a person confirms it on approve. */
export type SuggestedTrade = {
  problem_category_uuid: string;
  slug: string;
  label: string;
};

/** One row of `GET api/vendor-work-approvals`, and the body `PATCH .../work-approval` returns. */
export type VendorWorkApprovalRow = {
  vendor_uuid: string;
  organization_vendor_uuid: string;
  name: string;
  email: string | null;
  phone_number: string | null;
  is_active: boolean;
  pms_do_not_use: boolean;
  work_approval: VendorWorkApproval;
  linked_at: string | null;
  last_used_at: string | null;
  last_used_source: 'foresight' | 'pms' | null;
  recently_used: boolean;
  suggested_trades: SuggestedTrade[];
  /** The zip codes its service area covers today; shared across companies, so this screen only adds. */
  service_area_zip_codes: string[];
};

export type VendorWorkApprovalsState = {
  /** In the server's order: most recently used first, never used last, then by name. */
  rows: VendorWorkApprovalRow[];
  isLoading: boolean;
  hasLoaded: boolean;
  /** The server's message when the list failed to load; null when it loaded or is loading. */
  loadError: string | null;
  /** The latest list request, so a superseded (aborted) one can't overwrite its state. */
  requestId: string | null;
};

export const vendorWorkApprovalsInitialState: VendorWorkApprovalsState = {
  rows: [],
  isLoading: false,
  hasLoaded: false,
  loadError: null,
  requestId: null,
};

/** B14: the company's property management system, as the vendors list names it (null name: none connected). */
export type PropertyManagementSystem = { key: string | null; name: string | null };

/** One line of a vendor's "Landon rules" (`GET api/vendors/{uuid}/landon-rules`). */
export type VendorLandonRule = {
  key: string;
  label: string;
  sentence: string;
  /** Where the rule is changed; null when it is changed in the property management system. */
  settings_path: string | null;
};

export type InitialStateType = {
  /** Null until the first vendors list has loaded. */
  propertyManagementSystem: PropertyManagementSystem | null;
  isLoading: boolean;
  isDetailsLoading: boolean;
  data: {
    data: Array<string>;
    meta: VendorMetaType;
  };
  groupedData: {
    [key: string]: Array<string>;
  };
  source: {
    [key: string]: VendorDataType;
  };
  workApprovals: VendorWorkApprovalsState;
};

export const vendorInitialState: InitialStateType = {
  propertyManagementSystem: null,
  isLoading: false,
  isDetailsLoading: false,
  data: {
    data: [],
    meta: {
      next: '',
      per_page: 0,
      prev: '',
      total: 0,
      current_page: 0,
      from: 0,
      to: 0,
      last: '',
    },
  },
  groupedData: {},
  source: {},
  workApprovals: vendorWorkApprovalsInitialState,
};
